import { useEffect, useState, useMemo, useRef, memo } from "react";
import { useSelector } from "react-redux";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { MultiSelect } from "../ui/multi-select";
import { Filter, X, Link as LinkIcon, Trash2, Bookmark } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { fetchDesks, fetchAvailableDesks } from "../../services/crmServices/deskService";
import { searchUsers, fetchAvailableUsers } from "../../services/crmServices/userService";
import { fetchDistinctValues, fetchCountriesBySourceAffiliate, fetchStatusesBySourceAffiliate, fetchSourcesByAffiliate } from "../../services/crmServices/leadService";
import { getFullName } from "../../services/common/util";
import { selectCurrentFilters } from "../../store/slices/leadsSlice";
import { Country } from "country-state-city";
import { searchPositions } from "../../services/crmServices/positionService";
import { useAuth } from "../../hooks/useAuth";
import { getFilterShortcuts, createFilterShortcut, updateFilterShortcut, deleteFilterShortcut } from "../../services/crmServices/filterShortcutService";

export const LeadFilters = memo(function LeadFilters({ onFilter, onClear, allLeads = [] }) {
    const { user } = useAuth();
    const isAdmin = user?.position?.role === "admin";
    const isGeneralManager = user?.position?.role === "generalManager";
    const isFloorManager = user?.position?.role === "floorManager";
    const isDeskManager = user?.position?.role === "deskManager";
    const canFilterByAgent = isAdmin || isGeneralManager || isFloorManager || isDeskManager;
    // Get current filters from Redux state
    const savedFilters = useSelector(selectCurrentFilters);
    const leadStatuses = useSelector(selectLeadStatuses);

    const [filters, setFilters] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const hasInitializedRef = useRef(false);
    const [showSaveShortcutModal, setShowSaveShortcutModal] = useState(false);
    const [shortcutName, setShortcutName] = useState("");
    const [savedShortcuts, setSavedShortcuts] = useState([]);

    const [allDesks, setAllDesks] = useState([]);
    const [allAgents, setAllAgents] = useState([]); // For agent filter (actual agents)
    const [allStaff, setAllStaff] = useState([]); // For prevCommManager filter (non-agents)
    const [allFloorManagers, setAllFloorManagers] = useState([]);
    const [allGeneralManagers, setAllGeneralManagers] = useState([]);
    const [sources, setSources] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);

    // Add refs to cache positions and distinct values to avoid multiple API calls
    const agentPositionsRef = useRef(null);
    const floorManagerPositionsRef = useRef(null);
    const generalManagerPositionsRef = useRef(null);
    const distinctCountriesRef = useRef(null);
    const loadingSourcesByAffiliateRef = useRef(false);
    const sourcesSetByAffiliateRef = useRef(false);

    // Initialize filters with saved state only when sheet first opens
    useEffect(() => {
        if (!isOpen) {
            // Reset initialization flag when sheet closes
            hasInitializedRef.current = false;
            sourcesSetByAffiliateRef.current = false; // Reset flag when sheet closes
            return;
        }
        
        // Only initialize once when sheet opens, not on every render
        if (hasInitializedRef.current) return;
        hasInitializedRef.current = true;
        
        const processedFilters = savedFilters ? { ...savedFilters } : {};
        
        // Remove excludeCountries - we're using country (include) filter now
        if (processedFilters.excludeCountries) {
            delete processedFilters.excludeCountries;
        }
        
        // Ensure country filter is an array (can be empty - no countries selected by default)
        if (processedFilters.country && !Array.isArray(processedFilters.country)) {
            processedFilters.country = [processedFilters.country];
        }
        
        // Only update filters when sheet first opens
        setFilters(processedFilters);
    }, [isOpen, savedFilters]);

    // OPTIMIZED: Load filter data in parallel where possible
    useEffect(() => {
        const loadFilterData = async () => {
            try {
                // STEP 1: Fetch all positions once (both agent, floorManager, and generalManager)
                const [agentPositionsResponse, floorManagerPositionsResponse, generalManagerPositionsResponse] = await Promise.all([
                    searchPositions({ role: "agent" }),
                    isAdmin ? searchPositions({ role: "floorManager" }) : Promise.resolve({ data: [] }),
                    isAdmin ? searchPositions({ role: "generalManager" }) : Promise.resolve({ data: [] })
                ]);
                
                // Cache positions in refs
                agentPositionsRef.current = agentPositionsResponse?.data || [];
                floorManagerPositionsRef.current = floorManagerPositionsResponse?.data || [];
                generalManagerPositionsRef.current = generalManagerPositionsResponse?.data || [];
                
                // STEP 2: Parallel fetch for independent data (desks, users, floor managers, general managers)
                const promises = [
                    // Use fetchAvailableDesks for GM, FM, DM (backend filters automatically)
                    // Use fetchDesks for admin (sees everything)
                    isAdmin ? fetchDesks() : fetchAvailableDesks(),
                    getUsers(agentPositionsRef.current) // Pass cached positions
                ];
                
                if (isAdmin) {
                    promises.push(getFloorManagers(floorManagerPositionsRef.current)); // Pass cached positions
                    promises.push(getGeneralManagers(generalManagerPositionsRef.current)); // Pass cached positions
                } else if (isGeneralManager) {
                    // GM should only see floor managers who report to them
                    promises.push(getFloorManagersForGM(floorManagerPositionsRef.current));
                }
                
                const results = await Promise.all(promises);
                const desksResponse = results[0];

                // Process desks
                if (desksResponse?.data?.length) {
                    // For GM, FM, DM: backend already filtered desks via fetchAvailableDesks
                    // For admin: fetchDesks returns all desks
                    // No additional filtering needed
                    setAllDesks(desksResponse.data);
                    
                    // After desks are loaded, fetch agents based on user role
                    if (canFilterByAgent) {
                        getAgents(desksResponse.data, agentPositionsRef.current); // Pass cached positions
                    }
                }

                // Users and floor managers are already set by their respective functions
            } catch (error) {
                console.error("Error loading filter data:", error);
            }
        };
        if (isOpen) {
            loadFilterData();
        }
    }, [isAdmin, isGeneralManager, isDeskManager, isFloorManager, canFilterByAgent, user._id, user.position.floor, isOpen]);

    // Updated to use cached agent positions only (no fallback fetch)
    const getUsers = async (agentPositions = null) => {
        // Use cached positions - should always be available from useEffect
        const agentPositionsData = agentPositions || agentPositionsRef.current || [];
        
        const staffResponse = await searchUsers({ 
            position: { $nin: agentPositionsData.map((pos) => pos._id) } 
        });
        if (staffResponse?.data?.length) {
            setAllStaff(staffResponse.data);
        }
    };

    // Updated to use cached agent positions only (no fallback fetch)
    const getAgents = async (desks = [], agentPositions = null) => {
        try {
            let agentsResponse;
            
            // Use cached positions - should always be available from useEffect
            const agentPositionsData = agentPositions || agentPositionsRef.current || [];
            
            if (isAdmin) {
                // Admin sees all users
                agentsResponse = await fetchAvailableUsers();
            } else if (isGeneralManager) {
                // General manager sees agents from all desks under their floor managers and desk managers
                // The backend /user/v1/available endpoint already handles GM logic, but returns all users
                // So we need to filter to only agents
                const allUsersResponse = await fetchAvailableUsers();
                
                if (allUsersResponse?.data?.length) {
                    if (agentPositionsData.length > 0) {
                        // Filter to only include users with agent positions
                        const agentPositionIds = agentPositionsData.map((pos) => pos._id?.toString() || pos._id);
                        const filteredAgents = allUsersResponse.data.filter(user => {
                            const userPositionId = user.position?._id?.toString() || user.position?._id || user.position;
                            const isAgent = agentPositionIds.includes(userPositionId?.toString());
                            return isAgent;
                        });
                        agentsResponse = { data: filteredAgents };
                    } else {
                        // Fallback: Filter by position.role if positions not loaded
                        const filteredAgents = allUsersResponse.data.filter(user => {
                            return user.position?.role === "agent";
                        });
                        agentsResponse = { data: filteredAgents };
                    }
                }
            } else if (isFloorManager) {
                // Floor manager sees agents from their managed desks
                const managedDesks = desks.filter(desk => {
                    const floorManagerId = desk.floorManager?._id || desk.floorManager;
                    return (floorManagerId === user._id || floorManagerId?.toString() === user._id?.toString()) &&
                           desk.floor === user.position.floor;
                });
                const managedDeskIds = managedDesks.map(desk => desk._id?._id || desk._id).filter(Boolean);
                
                if (managedDeskIds.length > 0 && agentPositionsData.length > 0) {
                    agentsResponse = await searchUsers({ 
                        position: { $in: agentPositionsData.map((pos) => pos._id) },
                        agentDesk: { $in: managedDeskIds }
                    });
                }
            } else if (isDeskManager) {
                // Desk manager sees agents from their managed desks only
                const managedDesks = desks.filter(desk => {
                    // Handle both populated object and ID string
                    const managerId = desk.manager?._id || desk.manager;
                    const userId = user._id?.toString();
                    const managerIdStr = managerId?.toString();
                    return managerIdStr === userId;
                });
                
                // Normalize desk IDs to strings for comparison
                const managedDeskIds = managedDesks.map(desk => {
                    const deskId = desk._id?._id || desk._id;
                    return deskId?.toString();
                }).filter(Boolean);
                
                if (managedDeskIds.length > 0 && agentPositionsData.length > 0) {
                    agentsResponse = await searchUsers({ 
                        position: { $in: agentPositionsData.map((pos) => pos._id) },
                        agentDesk: { $in: managedDeskIds }
                    });
                }
            }
            
            if (agentsResponse?.data?.length) {
                setAllAgents(agentsResponse.data);
            } else {
                setAllAgents([]);
            }
        } catch (error) {
            console.error("Error fetching agents:", error);
            setAllAgents([]);
        }
    };

    // Updated to use cached floor manager positions only (no fallback fetch)
    const getFloorManagers = async (floorManagerPositions = null) => {
        // Use cached positions - should always be available from useEffect
        const floorManagerPositionsData = floorManagerPositions || floorManagerPositionsRef.current || [];
        
        if (floorManagerPositionsData.length > 0) {
            const floorManagersResponse = await searchUsers({
                position: { $in: floorManagerPositionsData.map((pos) => pos._id) }
            });
            if (floorManagersResponse?.data?.length) {
                setAllFloorManagers(floorManagersResponse.data);
            }
        }
    };

    const getGeneralManagers = async (generalManagerPositions = null) => {
        // Use cached positions - should always be available from useEffect
        const generalManagerPositionsData = generalManagerPositions || generalManagerPositionsRef.current || [];
        
        if (generalManagerPositionsData.length > 0) {
            const generalManagersResponse = await searchUsers({
                position: { $in: generalManagerPositionsData.map((pos) => pos._id) }
            });
            if (generalManagersResponse?.data?.length) {
                setAllGeneralManagers(generalManagersResponse.data);
            }
        }
    };

    // Get floor managers who report directly to this General Manager
    const getFloorManagersForGM = async (floorManagerPositions = null) => {
        const floorManagerPositionsData = floorManagerPositions || floorManagerPositionsRef.current || [];
        
        if (floorManagerPositionsData.length > 0) {
            // Find floor managers who report to this GM
            const floorManagersResponse = await searchUsers({
                position: { $in: floorManagerPositionsData.map((pos) => pos._id) },
                reportsTo: user._id
            });
            if (floorManagersResponse?.data?.length) {
                setAllFloorManagers(floorManagersResponse.data);
            } else {
                setAllFloorManagers([]);
            }
        }
    };

    // OPTIMIZED: Fetch distinct sources, affiliates, and countries in parallel
    useEffect(() => {
        const loadDistinctValues = async () => {
            try {
                // Get current affiliate value - don't load sources if affiliate is selected
                // Sources will be loaded by the affiliate-specific useEffect
                const currentAffiliate = filters.affiliate;
                const hasAffiliate = currentAffiliate && Array.isArray(currentAffiliate) && currentAffiliate.length > 0;
                
                // Parallel fetch for distinct values (sources only if no affiliate, affiliates, countries)
                const promises = [
                    hasAffiliate ? Promise.resolve(null) : fetchDistinctValues('source'), // Skip sources if affiliate is selected
                    fetchDistinctValues('affiliate'),
                    fetchDistinctValues('country')
                ];
                
                const [sourcesResponse, affiliatesResponse, countriesResponse] = await Promise.all(promises);
                
                // Only set sources if we fetched them AND no affiliate is selected AND affiliate useEffect hasn't set sources
                // This prevents race condition where affiliate useEffect sets filtered sources but this one overwrites them
                if (!hasAffiliate && sourcesResponse?.data && !sourcesSetByAffiliateRef.current) {
                    setSources(sourcesResponse.data.map((source) => ({ label: source, value: source })));
                    sourcesSetByAffiliateRef.current = false; // Reset flag
                }
                
                if (affiliatesResponse?.data) {
                    setAffiliates(affiliatesResponse.data.map((affiliate) => ({ label: affiliate, value: affiliate })));
                }
                
                // Cache countries for later use
                if (countriesResponse?.data && countriesResponse.data.length > 0) {
                    const dbCountries = countriesResponse.data.map((country) => ({
                        label: country,
                        value: country
                    }));
                    
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    
                    const countryMap = new Map();
                    dbCountries.forEach(country => {
                        countryMap.set(country.value, country);
                    });
                    libraryCountries.forEach(country => {
                        if (!countryMap.has(country.value)) {
                            countryMap.set(country.value, country);
                        }
                    });
                    
                    const combinedCountries = Array.from(countryMap.values()).sort((a, b) => 
                        a.label.localeCompare(b.label)
                    );
                    
                    distinctCountriesRef.current = combinedCountries;
                    setCountryOptions(combinedCountries);
                } else {
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    distinctCountriesRef.current = libraryCountries;
                    setCountryOptions(libraryCountries);
                }
            } catch (error) {
                console.error("Error fetching distinct values:", error);
                // Fallback to extracting from allLeads if API fails
                // BUT: Don't set sources if affiliate is selected (let the affiliate useEffect handle it)
                const currentAffiliate = filters.affiliate;
                const hasAffiliate = currentAffiliate && Array.isArray(currentAffiliate) && currentAffiliate.length > 0;
                
                if (allLeads.length > 0) {
                    // Only set sources if no affiliate is selected AND affiliate useEffect hasn't set sources
                    if (!hasAffiliate && !sourcesSetByAffiliateRef.current) {
                        const uniqueSources = [...new Set(allLeads.map((lead) => lead.source).filter(Boolean))];
                        setSources(uniqueSources.map((source) => ({ label: source, value: source })));
                        sourcesSetByAffiliateRef.current = false; // Reset flag
                    }
                    
                    const uniqueAffiliates = [...new Set(allLeads.map((lead) => lead.affiliate).filter(Boolean))];
                    setAffiliates(uniqueAffiliates.map((affiliate) => ({ label: affiliate, value: affiliate })));
                    
                    const uniqueCountries = [...new Set(allLeads.map((lead) => lead.country).filter(Boolean))];
                    
                    // For countries, combine with library countries
                    const dbCountries = uniqueCountries.map((country) => ({
                        label: country,
                        value: country
                    }));
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    
                    const countryMap = new Map();
                    dbCountries.forEach(country => countryMap.set(country.value, country));
                    libraryCountries.forEach(country => {
                        if (!countryMap.has(country.value)) {
                            countryMap.set(country.value, country);
                        }
                    });
                    
                    distinctCountriesRef.current = Array.from(countryMap.values()).sort((a, b) => 
                        a.label.localeCompare(b.label)
                    );
                    setCountryOptions(distinctCountriesRef.current);
                } else {
                    // Final fallback: just use library countries
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    distinctCountriesRef.current = libraryCountries;
                    setCountryOptions(libraryCountries);
                }
            }
        };
        
        if (isOpen) {
            loadDistinctValues();
        }
    }, [isOpen]); // Only run when sheet opens - affiliate useEffect handles source filtering

    // Create a stable key for source/affiliate changes
    const sourceAffiliateKey = useMemo(() => {
        const sourceKey = filters.source && Array.isArray(filters.source) ? [...filters.source].sort().join(',') : '';
        const affiliateKey = filters.affiliate && Array.isArray(filters.affiliate) ? [...filters.affiliate].sort().join(',') : '';
        return `${sourceKey}|${affiliateKey}`;
    }, [filters.source, filters.affiliate]);

    // Create a stable key for affiliate changes
    const affiliateKey = useMemo(() => {
        if (filters.affiliate && Array.isArray(filters.affiliate) && filters.affiliate.length > 0) {
            return [...filters.affiliate].sort().join(',');
        }
        return '';
    }, [filters.affiliate]);

    // Fetch sources filtered by affiliate when affiliate is selected
    useEffect(() => {
        const loadSourcesByAffiliate = async () => {
            // Only fetch if sheet is open
            if (!isOpen) {
                return;
            }

            // Prevent concurrent loads
            if (loadingSourcesByAffiliateRef.current) {
                return;
            }

            // Get current affiliate value from state
            const currentAffiliate = filters.affiliate;
            const hasAffiliate = currentAffiliate && Array.isArray(currentAffiliate) && currentAffiliate.length > 0;

            // If affiliate is selected, fetch sources for that affiliate
            if (hasAffiliate) {
                loadingSourcesByAffiliateRef.current = true;
                sourcesSetByAffiliateRef.current = true; // Mark that we're setting sources by affiliate
                try {
                    const sourcesResponse = await fetchSourcesByAffiliate(currentAffiliate);

                    if (sourcesResponse?.data && sourcesResponse.data.length > 0) {
                        // Format sources
                        const sourcesList = sourcesResponse.data.map((source) => ({
                            label: source,
                            value: source
                        }));
                        setSources(sourcesList);
                        
                        // Clear any selected sources that don't belong to the selected affiliate(s)
                        setFilters(prev => {
                            const currentSelectedSources = prev.source || [];
                            if (currentSelectedSources.length > 0) {
                                const validSourceValues = sourcesList.map(s => s.value);
                                const validSelectedSources = currentSelectedSources.filter(source => 
                                    validSourceValues.includes(source)
                                );
                                
                                // Only update if we need to remove invalid selections
                                if (validSelectedSources.length !== currentSelectedSources.length) {
                                    return {
                                        ...prev,
                                        source: validSelectedSources
                                    };
                                }
                            }
                            return prev;
                        });
                    } else {
                        // If no sources found for this affiliate, set empty array and clear selected sources
                        setSources([]);
                        setFilters(prev => {
                            if (prev.source && prev.source.length > 0) {
                                return {
                                    ...prev,
                                    source: []
                                };
                            }
                            return prev;
                        });
                    }
                } catch (error) {
                    console.error("Error fetching sources by affiliate:", error);
                    // On error, don't fallback to all sources - keep empty or show error
                    setSources([]);
                } finally {
                    loadingSourcesByAffiliateRef.current = false;
                }
            } else {
                // No affiliate selected - fetch all sources
                sourcesSetByAffiliateRef.current = false; // Reset flag when no affiliate
                try {
                    const allSourcesResponse = await fetchDistinctValues('source');
                    if (allSourcesResponse?.data) {
                        setSources(allSourcesResponse.data.map((source) => ({ label: source, value: source })));
                    }
                } catch (error) {
                    console.error("Error fetching all sources:", error);
                }
            }
        };

        loadSourcesByAffiliate();
    }, [isOpen, filters.affiliate]);

    // Fetch countries with counts when source or affiliate is selected
    useEffect(() => {
        const loadCountriesWithCounts = async () => {
            // Only fetch if sheet is open
            if (!isOpen) {
                return;
            }

            const hasSource = filters.source && Array.isArray(filters.source) && filters.source.length > 0;
            const hasAffiliate = filters.affiliate && Array.isArray(filters.affiliate) && filters.affiliate.length > 0;

            // If source or affiliate is selected, fetch countries with counts
            if (hasSource || hasAffiliate) {
                try {
                    const countriesResponse = await fetchCountriesBySourceAffiliate(
                        hasSource ? filters.source : [],
                        hasAffiliate ? filters.affiliate : []
                    );

                    if (countriesResponse?.data && countriesResponse.data.length > 0) {
                        // Format countries with counts
                        const countriesWithCounts = countriesResponse.data.map((item) => ({
                            label: item.country,
                            value: item.country,
                            count: item.count
                        }));

                        // Sort alphabetically by country name
                        const sortedCountries = countriesWithCounts.sort((a, b) =>
                            a.value.localeCompare(b.value)
                        );

                        setCountryOptions(sortedCountries);
                    } else {
                        // No countries found for this source/affiliate
                        setCountryOptions([]);
                    }
                } catch (error) {
                    console.error("Error fetching countries by source/affiliate:", error);
                    // On error, fall back to cached countries
                    if (distinctCountriesRef.current) {
                        setCountryOptions(distinctCountriesRef.current);
                    }
                }
            } else {
                // No source or affiliate selected - use cached countries (no API call)
                if (distinctCountriesRef.current) {
                    setCountryOptions(distinctCountriesRef.current);
                } else {
                    // Fallback: reload if cache is empty (shouldn't happen)
                    const loadNormalCountries = async () => {
                        try {
                            const countriesResponse = await fetchDistinctValues('country');
                            if (countriesResponse?.data && countriesResponse.data.length > 0) {
                                const dbCountries = countriesResponse.data.map((country) => ({
                                    label: country,
                                    value: country
                                }));

                                const libraryCountries = Country.getAllCountries().map((country) => ({
                                    label: country.name,
                                    value: country.name
                                }));

                                const countryMap = new Map();
                                dbCountries.forEach(country => {
                                    countryMap.set(country.value, country);
                                });
                                libraryCountries.forEach(country => {
                                    if (!countryMap.has(country.value)) {
                                        countryMap.set(country.value, country);
                                    }
                                });

                                const combinedCountries = Array.from(countryMap.values()).sort((a, b) =>
                                    a.label.localeCompare(b.label)
                                );

                                distinctCountriesRef.current = combinedCountries;
                                setCountryOptions(combinedCountries);
                            } else {
                                const libraryCountries = Country.getAllCountries().map((country) => ({
                                    label: country.name,
                                    value: country.name
                                }));
                                distinctCountriesRef.current = libraryCountries;
                                setCountryOptions(libraryCountries);
                            }
                        } catch (error) {
                            console.error("Error loading normal countries:", error);
                        }
                    };
                    loadNormalCountries();
                }
            }
        };

        // Debounce to avoid too many API calls
        const timeoutId = setTimeout(() => {
            loadCountriesWithCounts();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [sourceAffiliateKey, isOpen, filters.source, filters.affiliate]); // Watch for source/affiliate changes

    // State for status options with counts
    const [statusOptionsWithCounts, setStatusOptionsWithCounts] = useState([]);

    // Calculate or fetch status counts based on source/affiliate filters (similar to countries)
    useEffect(() => {
        const loadStatusCounts = async () => {
            // Only fetch if sheet is open
            if (!isOpen) {
                return;
            }

            const hasSource = filters.source && Array.isArray(filters.source) && filters.source.length > 0;
            const hasAffiliate = filters.affiliate && Array.isArray(filters.affiliate) && filters.affiliate.length > 0;

            if (!leadStatuses || leadStatuses.length === 0) {
                setStatusOptionsWithCounts(leadStatuses || []);
                return;
            }

            // If source or affiliate is selected, fetch status counts from API (not just current page)
            if (hasSource || hasAffiliate) {
                try {
                    const statusesResponse = await fetchStatusesBySourceAffiliate(
                        hasSource ? filters.source : [],
                        hasAffiliate ? filters.affiliate : []
                    );

                    if (statusesResponse?.data && statusesResponse.data.length > 0) {
                        // Create a map of status counts from API response
                        const statusCountsMap = new Map();
                        statusesResponse.data.forEach(item => {
                            statusCountsMap.set(item.status, item.count);
                        });

                        // Map leadStatuses with counts from API
                        const statusesWithCounts = leadStatuses.map(status => {
                            const statusValue = status.value || status;
                            return {
                                ...status,
                                count: statusCountsMap.get(statusValue) || 0
                            };
                        });

                        setStatusOptionsWithCounts(statusesWithCounts);
                    } else {
                        // If API returns empty, set all counts to 0
                        setStatusOptionsWithCounts(leadStatuses.map(status => ({
                            ...status,
                            count: 0
                        })));
                    }
                } catch (error) {
                    console.error("Error fetching status counts:", error);
                    // Fallback to calculating from allLeads on error
                    let filteredLeads = allLeads;
                    if (hasSource) {
                        filteredLeads = filteredLeads.filter(lead => 
                            lead.source && filters.source.includes(lead.source)
                        );
                    }
                    if (hasAffiliate) {
                        filteredLeads = filteredLeads.filter(lead => 
                            lead.affiliate && filters.affiliate.includes(lead.affiliate)
                        );
                    }
                    const statusCounts = new Map();
                    filteredLeads.forEach(lead => {
                        if (lead.status) {
                            const statusValue = typeof lead.status === 'string' ? lead.status : lead.status?.value || lead.status;
                            statusCounts.set(statusValue, (statusCounts.get(statusValue) || 0) + 1);
                        }
                    });
                    const statusesWithCounts = leadStatuses.map(status => {
                        const statusValue = status.value || status;
                        return {
                            ...status,
                            count: statusCounts.get(statusValue) || 0
                        };
                    });
                    setStatusOptionsWithCounts(statusesWithCounts);
                }
            } else {
                // No source/affiliate filter - calculate from allLeads (current page only)
                let filteredLeads = allLeads;
                const statusCounts = new Map();
                filteredLeads.forEach(lead => {
                    if (lead.status) {
                        const statusValue = typeof lead.status === 'string' ? lead.status : lead.status?.value || lead.status;
                        statusCounts.set(statusValue, (statusCounts.get(statusValue) || 0) + 1);
                    }
                });
                const statusesWithCounts = leadStatuses.map(status => {
                    const statusValue = status.value || status;
                    return {
                        ...status,
                        count: statusCounts.get(statusValue) || 0
                    };
                });
                setStatusOptionsWithCounts(statusesWithCounts);
            }
        };

        loadStatusCounts();
    }, [isOpen, leadStatuses, allLeads, filters.source, filters.affiliate]);

    const handleApplyFilters = () => {
        const processedFilters = { ...filters };
        
        // Remove excludeCountries - we're using country (include) filter now
        delete processedFilters.excludeCountries;
        
        // If no countries are selected, remove country filter (show all)
        if (!processedFilters.country || (Array.isArray(processedFilters.country) && processedFilters.country.length === 0)) {
            delete processedFilters.country;
        }
        
        // Ensure country is always an array if it exists
        if (processedFilters.country && !Array.isArray(processedFilters.country)) {
            processedFilters.country = [processedFilters.country];
        }
        
        // Remove any undefined or empty array filters
        Object.keys(processedFilters).forEach(key => {
            if (processedFilters[key] === undefined || 
                processedFilters[key] === null ||
                (Array.isArray(processedFilters[key]) && processedFilters[key].length === 0)) {
                delete processedFilters[key];
            }
        });
        
        onFilter(processedFilters);
        setIsOpen(false);
    };

    const handleClearFilters = () => {
        setFilters({});
        onClear();
        setIsOpen(false);
    };

    // Load saved shortcuts from API
    useEffect(() => {
        const loadShortcuts = async () => {
            try {
                const response = await getFilterShortcuts();
                if (response?.data) {
                    // Map API response to match expected format
                    const mappedShortcuts = response.data.map(shortcut => ({
                        id: shortcut._id,
                        name: shortcut.name,
                        filters: shortcut.filters,
                        createdAt: shortcut.createdAt,
                        updatedAt: shortcut.updatedAt
                    }));
                    setSavedShortcuts(mappedShortcuts);
                }
            } catch (error) {
                console.error("Error loading shortcuts:", error);
                // Fallback to localStorage for migration
                try {
                    const shortcuts = localStorage.getItem('leadFilterShortcuts');
                    if (shortcuts) {
                        const parsed = JSON.parse(shortcuts);
                        setSavedShortcuts(parsed);
                        // Optionally migrate to backend
                        if (parsed.length > 0) {
                            migrateShortcutsToBackend(parsed);
                        }
                    }
                } catch (localError) {
                    console.error("Error loading from localStorage:", localError);
                }
            }
        };
        loadShortcuts();
    }, []);

    // Helper function to migrate localStorage shortcuts to backend
    const migrateShortcutsToBackend = async (localShortcuts) => {
        try {
            for (const shortcut of localShortcuts) {
                await createFilterShortcut({
                    name: shortcut.name,
                    filters: shortcut.filters
                });
            }
            // Clear localStorage after migration
            localStorage.removeItem('leadFilterShortcuts');
        } catch (error) {
            console.error("Error migrating shortcuts:", error);
        }
    };

    const handleSaveShortcut = () => {
        // Check if there are any filters to save
        const hasFilters = Object.keys(filters).length > 0 && 
            Object.values(filters).some(value => {
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === 'string') return value.trim() !== '';
                return value !== null && value !== undefined;
            });

        if (!hasFilters) {
            toast.error("Please set some filters before saving a shortcut");
            return;
        }

        setShowSaveShortcutModal(true);
    };

    const handleConfirmSaveShortcut = async () => {
        if (!shortcutName.trim()) {
            toast.error("Please enter a name for the shortcut");
            return;
        }

        try {
            // Check if shortcut with same name exists
            const existingShortcut = savedShortcuts.find(
                s => s.name.toLowerCase() === shortcutName.trim().toLowerCase()
            );

            let response;
            if (existingShortcut) {
                // Update existing shortcut
                response = await updateFilterShortcut(existingShortcut.id, {
                    name: shortcutName.trim(),
                    filters: { ...filters }
                });
                toast.success("Shortcut updated successfully");
            } else {
                // Create new shortcut
                response = await createFilterShortcut({
                    name: shortcutName.trim(),
                    filters: { ...filters }
                });
                toast.success("Shortcut saved successfully");
            }

            // Refresh shortcuts list
            const shortcutsResponse = await getFilterShortcuts();
            if (shortcutsResponse?.data) {
                const mappedShortcuts = shortcutsResponse.data.map(shortcut => ({
                    id: shortcut._id,
                    name: shortcut.name,
                    filters: shortcut.filters,
                    createdAt: shortcut.createdAt,
                    updatedAt: shortcut.updatedAt
                }));
                setSavedShortcuts(mappedShortcuts);
            }

            setShortcutName("");
            setShowSaveShortcutModal(false);
        } catch (error) {
            console.error("Error saving shortcut:", error);
            toast.error(error.response?.data?.message || "Failed to save shortcut");
        }
    };

    const handleLoadShortcut = (shortcut) => {
        setFilters(shortcut.filters);
        toast.success(`Loaded shortcut: ${shortcut.name}`);
    };

    const handleDeleteShortcut = async (shortcutId) => {
        try {
            await deleteFilterShortcut(shortcutId);
            
            // Update local state
            const updatedShortcuts = savedShortcuts.filter(s => s.id !== shortcutId);
            setSavedShortcuts(updatedShortcuts);
            toast.success("Shortcut deleted");
        } catch (error) {
            console.error("Error deleting shortcut:", error);
            toast.error(error.response?.data?.message || "Failed to delete shortcut");
        }
    };

    // Helper function to update filter values
    const updateFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    // Count active filters for display
    const activeFiltersCount = Object.keys(filters).filter((key) => {
        const value = filters[key];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value && value.toString().trim() !== "";
    }).length;

    return (
        <>
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
                <Button variant="outline" className="relative">
                    <Filter className="h-4 w-4 mr-2" />
                    Filter
                    {activeFiltersCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {activeFiltersCount}
                        </span>
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-96 overflow-y-auto">
                <SheetHeader>
                    <SheetTitle>Filter Leads</SheetTitle>
                    <SheetDescription>Apply filters to refine your lead search results</SheetDescription>
                </SheetHeader>

                <div className="py-6 space-y-6">
                    {/* Date Filters */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Date Range</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="dateFrom">Registered From</Label>
                                <Input
                                    id="dateFrom"
                                    type="date"
                                    value={filters.registeredFrom || ""}
                                    onChange={(e) => updateFilter("registeredFrom", e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="dateTo">Registered To</Label>
                                <Input
                                    id="dateTo"
                                    type="date"
                                    value={filters.registeredTo || ""}
                                    onChange={(e) => updateFilter("registeredTo", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Geographic Filters */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Geographic</Label>
                        <div className="space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <Label>Country</Label>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        type="button"
                                        onClick={() => {
                                            const allCountryValues = countryOptions.map(c => c.value);
                                            const currentSelected = filters.country || [];
                                            if (currentSelected.length === countryOptions.length) {
                                                // Deselect all
                                                updateFilter("country", []);
                                            } else {
                                                // Select all
                                                updateFilter("country", allCountryValues);
                                            }
                                        }}
                                        className="h-7 text-xs"
                                    >
                                        {(filters.country || []).length === countryOptions.length ? "Deselect All" : "Select All"}
                                    </Button>
                                </div>
                                <MultiSelect
                                    options={countryOptions}
                                    selected={filters.country || []}
                                    onChange={(selected) => updateFilter("country", selected)}
                                    placeholder="Select countries to include"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Select countries to filter by. Leave empty to show all countries.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Source & Affiliate Filters */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Source & Affiliate</Label>
                        <div className="space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Source</Label>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            type="button"
                                            onClick={() => {
                                                const allSourceValues = sources.map(s => s.value);
                                                const currentSelected = filters.source || [];
                                                if (currentSelected.length === sources.length) {
                                                    updateFilter("source", []);
                                                } else {
                                                    updateFilter("source", allSourceValues);
                                                }
                                            }}
                                            className="h-6 text-xs"
                                        >
                                            {(filters.source || []).length === sources.length && sources.length > 0 ? "Deselect All" : "Select All"}
                                        </Button>
                                        {(filters.source || []).length > 0 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => updateFilter("source", [])}
                                                className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                            >
                                                <X className="h-3 w-3 mr-1" />
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <MultiSelect
                                    options={sources}
                                    selected={filters.source || []}
                                    onChange={(selected) => updateFilter("source", selected)}
                                    placeholder="Select sources"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Affiliate</Label>
                                    {(filters.affiliate || []).length > 0 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => updateFilter("affiliate", [])}
                                            className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            <X className="h-3 w-3 mr-1" />
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <MultiSelect
                                    options={affiliates}
                                    selected={filters.affiliate || []}
                                    onChange={(selected) => updateFilter("affiliate", selected)}
                                    placeholder="Select affiliates"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Status & Desk Filters */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Status & Desk</Label>
                        <div className="space-y-3">
                            {isAdmin && (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label>General Manager</Label>
                                        {(filters.generalManager || []).length > 0 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => updateFilter("generalManager", [])}
                                                className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                            >
                                                <X className="h-3 w-3 mr-1" />
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                    <MultiSelect
                                        options={[
                                            { label: "Unassigned", value: "unassigned" },
                                            ...allGeneralManagers.map((generalManager) => ({
                                                label: getFullName(generalManager),
                                                value: generalManager._id
                                            }))
                                        ]}
                                        selected={filters.generalManager || []}
                                        onChange={(selected) => updateFilter("generalManager", selected)}
                                        placeholder="Select general managers"
                                    />
                                </div>
                            )}
                            {isAdmin && (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label>Floor Manager</Label>
                                        {(filters.floorManager || []).length > 0 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => updateFilter("floorManager", [])}
                                                className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                            >
                                                <X className="h-3 w-3 mr-1" />
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                    <MultiSelect
                                        options={[
                                            { label: "Unassigned", value: "unassigned" },
                                            ...allFloorManagers.map((floorManager) => ({
                                                label: getFullName(floorManager),
                                                value: floorManager._id
                                            }))
                                        ]}
                                        selected={filters.floorManager || []}
                                        onChange={(selected) => updateFilter("floorManager", selected)}
                                        placeholder="Select floor managers"
                                    />
                                </div>
                            )}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Desk</Label>
                                    {(filters.desk || []).length > 0 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => updateFilter("desk", [])}
                                            className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            <X className="h-3 w-3 mr-1" />
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <MultiSelect
                                    options={[
                                        // Hide "Unassigned" option for desk managers
                                        ...(!isDeskManager ? [{ label: "Unassigned", value: "unassigned" }] : []),
                                        ...allDesks.map((desk) => ({ label: desk.name, value: desk._id }))
                                    ]}
                                    selected={filters.desk || []}
                                    onChange={(selected) => updateFilter("desk", selected)}
                                    placeholder="Select desks"
                                />
                            </div>
                            {canFilterByAgent && (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label>Agent</Label>
                                        <div className="flex items-center gap-1">
                                            {allAgents.length > 0 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        updateFilter(
                                                            "agent",
                                                            [
                                                                "unassigned",
                                                                ...allAgents.map((agent) => agent._id)
                                                            ]
                                                        )
                                                    }
                                                    className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                                >
                                                    Select all
                                                </Button>
                                            )}
                                            {(filters.agent || []).length > 0 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => updateFilter("agent", [])}
                                                    className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                                >
                                                    <X className="h-3 w-3 mr-1" />
                                                    Clear
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <MultiSelect
                                        options={[
                                            { label: "Unassigned", value: "unassigned" },
                                            ...allAgents.map((agent) => ({
                                                label: getFullName(agent),
                                                value: agent._id
                                            }))
                                        ]}
                                        selected={filters.agent || []}
                                        onChange={(selected) => updateFilter("agent", selected)}
                                        placeholder="Select agents"
                                    />
                                </div>
                            )}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Statuses</Label>
                                    <div className="flex items-center gap-1">
                                        {statusOptionsWithCounts.length > 0 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    updateFilter(
                                                        "statuses",
                                                        statusOptionsWithCounts.map(
                                                            (status) => status.value || status.label || status
                                                        )
                                                    )
                                                }
                                                className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                            >
                                                Select all
                                            </Button>
                                        )}
                                        {(filters.statuses || []).length > 0 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => updateFilter("statuses", [])}
                                                className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                            >
                                                <X className="h-3 w-3 mr-1" />
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <MultiSelect
                                    options={statusOptionsWithCounts}
                                    selected={filters.statuses || []}
                                    onChange={(selected) => updateFilter("statuses", selected)}
                                    placeholder="Select statuses"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Exclude Statuses</Label>
                                    {(filters.excludeStatuses || []).length > 0 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => updateFilter("excludeStatuses", [])}
                                            className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            <X className="h-3 w-3 mr-1" />
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <MultiSelect
                                    options={leadStatuses}
                                    selected={filters.excludeStatuses || []}
                                    onChange={(selected) => updateFilter("excludeStatuses", selected)}
                                    placeholder="Select statuses to exclude"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Lead Indicators */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Lead Indicators</Label>
                        <div className="space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Live Leads (L)</Label>
                                    {filters.isLive && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => updateFilter("isLive", null)}
                                            className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            <X className="h-3 w-3 mr-1" />
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <MultiSelect
                                    options={[
                                        { label: "Live (from affiliate)", value: "true" },
                                        { label: "Not Live", value: "false" }
                                    ]}
                                    selected={filters.isLive ? [filters.isLive] : []}
                                    onChange={(selected) => updateFilter("isLive", selected.length > 0 ? selected[0] : null)}
                                    placeholder="Filter by Live status"
                                    className="max-w-full"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Transferred Leads (T)</Label>
                                    {filters.hasBeenTransferred && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => updateFilter("hasBeenTransferred", null)}
                                            className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            <X className="h-3 w-3 mr-1" />
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <MultiSelect
                                    options={[
                                        { label: "Has been transferred", value: "true" },
                                        { label: "Never transferred", value: "false" }
                                    ]}
                                    selected={filters.hasBeenTransferred ? [filters.hasBeenTransferred] : []}
                                    onChange={(selected) => updateFilter("hasBeenTransferred", selected.length > 0 ? selected[0] : null)}
                                    placeholder="Filter by Transfer status"
                                    className="max-w-full"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Call Count Range</Label>
                                    {(filters.callCountRange || []).length > 0 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => updateFilter("callCountRange", [])}
                                            className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            <X className="h-3 w-3 mr-1" />
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <MultiSelect
                                    options={[
                                        { label: "0 calls", value: "0" },
                                        { label: "1 call", value: "1" },
                                        { label: "2 calls", value: "2" },
                                        { label: "�� 0-5 calls", value: "0-5" },
                                        { label: "�� 5-10 calls", value: "5-10" },
                                        { label: "🟠 10-25 calls", value: "10-25" },
                                        { label: "�� 25+ calls", value: "25+" }
                                    ]}
                                    selected={filters.callCountRange || []}
                                    onChange={(selected) => updateFilter("callCountRange", selected)}
                                    placeholder="Filter by call count"
                                    className="max-w-full"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Comment Date Range */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Comments Date Range</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="commentsFrom">Comments From</Label>
                                <Input
                                    id="commentsFrom"
                                    type="date"
                                    value={filters.commentsFrom || ""}
                                    onChange={(e) => updateFilter("commentsFrom", e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="commentsTo">Comments To</Label>
                                <Input
                                    id="commentsTo"
                                    type="date"
                                    value={filters.commentsTo || ""}
                                    onChange={(e) => updateFilter("commentsTo", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Cleaning Status (admin only) - filters leads that have had their history cleaned */}
                    {isAdmin && (
                        <div className="space-y-3">
                            <Label className="text-base font-semibold text-purple-700">Cleaning Status (Admin)</Label>
                            <div className="space-y-3">
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label>Cleaned?</Label>
                                        {filters.cleanedStatus && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => updateFilter("cleanedStatus", null)}
                                                className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                            >
                                                <X className="h-3 w-3 mr-1" />
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                    <MultiSelect
                                        options={[
                                            { label: "Only cleaned leads", value: "cleaned" },
                                            { label: "Only never-cleaned leads", value: "never" }
                                        ]}
                                        selected={filters.cleanedStatus ? [filters.cleanedStatus] : []}
                                        onChange={(selected) => updateFilter("cleanedStatus", selected.length > 0 ? selected[0] : null)}
                                        placeholder="All leads (default)"
                                        className="max-w-full"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="cleanedFrom">Cleaned From</Label>
                                        <Input
                                            id="cleanedFrom"
                                            type="date"
                                            value={filters.cleanedFrom || ""}
                                            onChange={(e) => updateFilter("cleanedFrom", e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="cleanedTo">Cleaned To</Label>
                                        <Input
                                            id="cleanedTo"
                                            type="date"
                                            value={filters.cleanedTo || ""}
                                            onChange={(e) => updateFilter("cleanedTo", e.target.value)}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500">
                                    Find leads whose history was wiped via Bulk Edit &rarr; Clean Lead History.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Previous Communication Manager */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Previous Communication</Label>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label>Prev. Comm. Staff</Label>
                                {(filters.prevCommManager || []).length > 0 && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => updateFilter("prevCommManager", [])}
                                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                    >
                                        <X className="h-3 w-3 mr-1" />
                                        Clear
                                    </Button>
                                )}
                            </div>
                            <MultiSelect
                                options={allStaff.map((staff) => ({ label: getFullName(staff), value: staff._id }))}
                                selected={filters.prevCommManager || []}
                                onChange={(selected) => updateFilter("prevCommManager", selected)}
                                placeholder="Select staff"
                            />
                        </div>
                    </div>
                </div>

                {/* Saved Shortcuts */}
                {savedShortcuts.length > 0 && (
                    <div className="border-t pt-4 mt-4">
                        <Label className="text-base font-semibold mb-3 block">Saved Shortcuts</Label>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {savedShortcuts.map((shortcut) => (
                                <div
                                    key={shortcut.id}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <button
                                        onClick={() => handleLoadShortcut(shortcut)}
                                        className="flex-1 text-left text-sm hover:text-blue-600 transition-colors"
                                    >
                                        <Bookmark className="h-3 w-3 inline mr-2 text-blue-500" />
                                        {shortcut.name}
                                    </button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteShortcut(shortcut.id)}
                                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        title="Delete shortcut"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex flex-col space-y-3">
                    <Button onClick={handleSaveShortcut} variant="link" className="justify-start p-0">
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Save Shortcut
                    </Button>
                    <div className="flex space-x-3">
                        <Button onClick={handleApplyFilters} className="flex-1 ">
                            Apply Filters
                        </Button>
                        <Button variant="outline" onClick={handleClearFilters} className="flex-1">
                            <X className="h-4 w-4 mr-2" />
                            Clear
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>

        {/* Save Shortcut Modal */}
        <Dialog open={showSaveShortcutModal} onOpenChange={setShowSaveShortcutModal}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Save Filter Shortcut</DialogTitle>
                    <DialogDescription>
                        Give this filter configuration a name to save it for quick access later
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div>
                        <Label htmlFor="shortcutName">Shortcut Name</Label>
                        <Input
                            id="shortcutName"
                            value={shortcutName}
                            onChange={(e) => setShortcutName(e.target.value)}
                            placeholder="e.g., Active Leads from US"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleConfirmSaveShortcut();
                                }
                            }}
                            autoFocus
                        />
                    </div>
                    <div className="flex space-x-3 justify-end">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowSaveShortcutModal(false);
                                setShortcutName("");
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmSaveShortcut}
                            disabled={!shortcutName.trim()}
                            className=""
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
});
